/**
 * SPCB Checkout Store Sync Module
 *
 * Professional solution for synchronizing WooCommerce Blocks store state
 * with the checkout UI. Ensures shipping methods are properly loaded and
 * selected on page load.
 *
 * This module solves the "necesita una opción de envío" error that occurs
 * when WooCommerce Blocks' React state isn't properly synced on initial load.
 *
 * Uses official WC Blocks Store API:
 * - wp.data.select('wc/store/cart')
 * - wp.data.dispatch('wc/store/cart')
 * - wp.data.subscribe()
 *
 * @package SuperPymeCheckoutBlocks
 * @since 1.8.62
 * @version 1.16.22 - Added pickup guards to prevent overwriting Local Pickup selection
 *                    Fixed getFirstAvailableRateId() to respect DOM selection
 *                    Removed "prefer shipping over pickup" logic that caused race conditions
 */

(function() {
    'use strict';

    // Debug mode
    const DEBUG = window.location.search.includes('spcb_debug=1');
    const log = (...args) => DEBUG && console.log('[SPCB Store Sync]', ...args);
    const SHIPPING_SYNC_ENABLED = window.spcbConfig?.enableShippingSync === true;

    // State tracking
    let storeReady = false;
    let syncAttempts = 0;
    const MAX_SYNC_ATTEMPTS = 10;
    const SYNC_RETRY_DELAY = 300;

    // ==========================================================================
    // v1.12.0: CHECKOUT PROCESSING LOCK
    // Prevents sync operations during order creation to avoid race conditions
    // that could cause duplicate items/shipping
    // ==========================================================================
    let checkoutProcessing = false;
    const processingListeners = new Set();

    /**
     * Check if checkout is currently processing
     * Uses both our flag AND the WC Blocks checkout store state
     */
    function isCheckoutProcessing() {
        if (checkoutProcessing) return true;

        // Also check WC Blocks checkout store
        try {
            const checkoutStore = window.wp?.data?.select?.('wc/store/checkout');
            if (checkoutStore) {
                const isProcessing = checkoutStore.isProcessing?.();
                const isComplete = checkoutStore.isComplete?.();
                if (isProcessing || isComplete) {
                    return true;
                }
            }
        } catch (e) {
            // Ignore errors
        }

        return false;
    }

    /**
     * Set checkout processing state
     */
    function setCheckoutProcessing(processing) {
        if (checkoutProcessing === processing) {
            return;
        }

        checkoutProcessing = processing;
        log('Checkout processing:', processing);

        processingListeners.forEach((listener) => {
            try {
                listener(processing);
            } catch (e) {
                // Ignore listener errors
            }
        });
    }

    // Listen for checkout events from WC Blocks
    function initCheckoutProcessingListener() {
        // Method 1: Subscribe to checkout store
        if (window.wp?.data?.subscribe) {
            let lastProcessing = false;

            window.wp.data.subscribe(() => {
                try {
                    const checkoutStore = window.wp.data.select('wc/store/checkout');
                    if (!checkoutStore) return;

                    const isProcessing = checkoutStore.isProcessing?.() || false;
                    const isBeforeProcessing = checkoutStore.isBeforeProcessing?.() || false;
                    const isAfterProcessing = checkoutStore.isAfterProcessing?.() || false;

                    const currentProcessing = isProcessing || isBeforeProcessing || isAfterProcessing;

                    if (currentProcessing !== lastProcessing) {
                        lastProcessing = currentProcessing;
                        setCheckoutProcessing(currentProcessing);
                    }
                } catch (e) {
                    // Ignore
                }
            });

            log('Checkout processing listener initialized via wp.data.subscribe');
        }

        // Method 2: Listen for DOM events (backup)
        document.addEventListener('click', (e) => {
            const placeOrderBtn = e.target.closest('.wc-block-components-checkout-place-order-button');
            if (placeOrderBtn) {
                setCheckoutProcessing(true);
                log('Place order button clicked - sync paused');

                // Safety timeout: resume after 30 seconds if something goes wrong
                setTimeout(() => {
                    if (checkoutProcessing) {
                        setCheckoutProcessing(false);
                        log('Safety timeout: resuming sync');
                    }
                }, 30000);
            }
        });

        log('Checkout processing listeners initialized');
    }

    // Expose globally for other modules
    window.spcbCheckoutLock = {
        isProcessing: isCheckoutProcessing,
        setProcessing: setCheckoutProcessing,
        subscribe: (listener) => {
            if (typeof listener !== 'function') {
                return () => {};
            }

            processingListeners.add(listener);
            return () => processingListeners.delete(listener);
        }
    };

    /**
     * Safe React input setter with checkout-processing guard
     */
    function safeSetReactInputValue(input, value, context = 'unknown') {
        if (!input) {
            log('safeSetReactInputValue blocked - missing input:', context);
            return false;
        }

        if (isCheckoutProcessing()) {
            log('safeSetReactInputValue blocked - checkout processing:', context, input.id || input.name);
            return false;
        }

        try {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                window.HTMLInputElement.prototype,
                'value'
            ).set;
            nativeInputValueSetter.call(input, value);

            const inputEvent = new InputEvent('input', {
                bubbles: true,
                cancelable: true,
                inputType: 'insertText',
                data: value,
            });
            input.dispatchEvent(inputEvent);

            const changeEvent = new Event('change', { bubbles: true, cancelable: true });
            input.dispatchEvent(changeEvent);

            return true;
        } catch (e) {
            log('safeSetReactInputValue error:', context, e.message);
            return false;
        }
    }

    window.spcbCheckoutLock.safeSetReactInputValue = safeSetReactInputValue;

    /**
     * Check if WooCommerce Blocks data store is available
     */
    function isStoreAvailable() {
        return !!(
            window.wp?.data?.select &&
            window.wp?.data?.dispatch &&
            window.wp?.data?.subscribe
        );
    }

    /**
     * Get the cart store selectors
     */
    function getCartStore() {
        if (!isStoreAvailable()) return null;
        try {
            return window.wp.data.select('wc/store/cart');
        } catch (e) {
            log('Error getting cart store:', e.message);
            return null;
        }
    }

    /**
     * Get the cart store dispatchers
     */
    function getCartDispatch() {
        if (!isStoreAvailable()) return null;
        try {
            return window.wp.data.dispatch('wc/store/cart');
        } catch (e) {
            log('Error getting cart dispatch:', e.message);
            return null;
        }
    }

    // =========================================================================
    // SHIPPING RATES SYNC
    // =========================================================================

    /**
     * Check if shipping rates are loaded and valid
     */
    function hasValidShippingRates() {
        const store = getCartStore();
        if (!store) return false;

        try {
            const shippingRates = store.getShippingRates?.();
            if (!shippingRates || shippingRates.length === 0) {
                return false;
            }

            const packageRates = shippingRates[0]?.shipping_rates || [];
            return packageRates.length > 0;
        } catch (e) {
            log('Error checking shipping rates:', e.message);
            return false;
        }
    }

    /**
     * Check if a shipping rate is selected in the store
     */
    function hasSelectedShippingRate() {
        const store = getCartStore();
        if (!store) return false;

        try {
            const shippingRates = store.getShippingRates?.();
            if (!shippingRates || shippingRates.length === 0) {
                return false;
            }

            const packageRates = shippingRates[0]?.shipping_rates || [];
            return packageRates.some(rate => rate.selected);
        } catch (e) {
            log('Error checking selected rate:', e.message);
            return false;
        }
    }

    /**
     * Check if a rate ID is a pickup method (not a shipping method)
     * v1.11.5: Used to prevent auto-selecting pickup when shipping methods exist
     */
    function isPickupMethod(rateId) {
        if (!rateId) return false;
        const pickupPrefixes = ['pickup_location', 'local_pickup'];
        return pickupPrefixes.some(prefix => rateId.startsWith(prefix));
    }

    /**
     * Check if a rate ID is an SPCB method
     */
    function isSpcbRate(rateId) {
        return typeof rateId === 'string' && rateId.startsWith('spcb_');
    }

    /**
     * Guard to prevent programmatic sync for non-SPCB rates
     * and avoid duplicate selections in a short window.
     */
    function shouldSkipProgrammaticSync(rateId, context = 'unknown') {
        if (!isSpcbRate(rateId)) {
            log('Skipping programmatic sync for non-SPCB rate:', rateId, context);
            return true;
        }

        const now = Date.now();
        const lock = window.spcbShippingSyncLock || { lastRateId: null, lastTs: 0 };
        if (lock.lastRateId === rateId && (now - lock.lastTs) < 500) {
            log('Shipping sync lock hit:', rateId, context);
            return true;
        }

        window.spcbShippingSyncLock = { lastRateId: rateId, lastTs: now };
        return false;
    }

    /**
     * Get the first available shipping rate ID
     * v1.16.22: CORREGIDO - Ahora respeta la selección del DOM como prioridad máxima
     * Se eliminó la preferencia de shipping sobre pickup que causaba race conditions
     */
    function getFirstAvailableRateId() {
        const store = getCartStore();
        if (!store) return null;

        try {
            const shippingRates = store.getShippingRates?.();
            if (!shippingRates || shippingRates.length === 0) {
                return null;
            }

            const packageRates = shippingRates[0]?.shipping_rates || [];
            if (packageRates.length === 0) {
                return null;
            }

            // v1.16.22: PRIORIDAD 1 - Verificar DOM primero (más confiable)
            // Si el usuario tiene un radio marcado, SIEMPRE respetarlo
            // Esto evita que la sincronización sobrescriba la selección del usuario
            const domSelectedRadio = document.querySelector(
                '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
            );
            if (domSelectedRadio && domSelectedRadio.value) {
                log('Respetando selección del DOM:', domSelectedRadio.value);
                return domSelectedRadio.value;
            }

            // PRIORIDAD 2 - Verificar store para rate seleccionado
            const selected = packageRates.find(r => r.selected);
            if (selected) {
                return selected.rate_id;
            }

            // PRIORIDAD 3 - Solo auto-seleccionar si no hay nada seleccionado
            // v1.16.22: ELIMINADA la preferencia de shipping sobre pickup
            // Esa lógica causaba conflictos con Local Pickup y race conditions
            // Simplemente retornar el primer rate disponible
            return packageRates[0]?.rate_id || null;
        } catch (e) {
            log('Error getting first rate:', e.message);
            return null;
        }
    }

    /**
     * Force select a shipping rate in the store
     * v1.14.8: Added isCheckoutProcessing check to prevent race conditions
     */
    function forceSelectShippingRate(rateId, packageIndex = 0) {
        if (!SHIPPING_SYNC_ENABLED) {
            log('forceSelectShippingRate disabled (official flow)');
            return false;
        }

        // v1.14.8: CRITICAL - Never change shipping during checkout processing
        if (isCheckoutProcessing()) {
            log('forceSelectShippingRate blocked - checkout processing');
            return false;
        }

        // v1.18.3: CRITICAL - Never interfere while branch-checkbox is refreshing
        if (window.spcbBranchRefreshInProgress) {
            log('forceSelectShippingRate blocked - branch refresh in progress');
            return false;
        }

        const dispatch = getCartDispatch();
        if (!dispatch || !rateId) return false;

        if (shouldSkipProgrammaticSync(rateId, 'forceSelectShippingRate')) {
            return false;
        }

        try {
            if (dispatch.selectShippingRate) {
                dispatch.selectShippingRate(rateId, packageIndex);
                log('Forced shipping rate selection:', rateId);
                return true;
            }
        } catch (e) {
            log('Error selecting shipping rate:', e.message);
        }
        return false;
    }

    /**
     * Invalidate cart store resolution to force refresh
     * v1.14.8: Added isCheckoutProcessing check to prevent race conditions
     */
    function invalidateCartStore() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('invalidateCartStore disabled (official flow)');
            return false;
        }

        // v1.14.8: CRITICAL - Never invalidate during checkout processing
        if (isCheckoutProcessing()) {
            log('invalidateCartStore blocked - checkout processing');
            return false;
        }

        const dispatch = getCartDispatch();
        if (!dispatch) return false;

        try {
            // Try different invalidation methods based on WC version
            if (dispatch.invalidateResolutionForStore) {
                dispatch.invalidateResolutionForStore();
                log('Cart store invalidated via invalidateResolutionForStore');
                return true;
            }

            if (dispatch.invalidateResolution) {
                dispatch.invalidateResolution('getCartData', []);
                log('Cart store invalidated via invalidateResolution');
                return true;
            }
        } catch (e) {
            log('Error invalidating cart store:', e.message);
        }
        return false;
    }

    /**
     * Trigger cart update via Store API REST endpoint
     * This is a more reliable way to force WC Blocks to refresh
     * v1.14.14: Added isCheckoutProcessing guard
     */
    async function triggerCartRefresh() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('triggerCartRefresh disabled (official flow)');
            return false;
        }

        // v1.14.14: Skip during checkout to prevent race conditions
        if (isCheckoutProcessing()) {
            log('triggerCartRefresh blocked - checkout processing');
            return false;
        }

        try {
            const response = await fetch('/wp-json/wc/store/v1/cart', {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'Nonce': window.wcBlocksMiddlewareConfig?.nonce || ''
                }
            });

            if (response.ok) {
                log('Cart refresh triggered via Store API');
                return true;
            }
        } catch (e) {
            log('Error triggering cart refresh:', e.message);
        }
        return false;
    }

    /**
     * Sync DOM shipping selection with store
     * This ensures the visual selection matches the store state
     * v1.14.8: Added isCheckoutProcessing check to prevent race conditions
     */
    function syncDomWithStore() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('syncDomWithStore disabled (official flow)');
            return;
        }

        // v1.14.8: CRITICAL - Never sync DOM during checkout processing
        if (isCheckoutProcessing()) {
            log('syncDomWithStore blocked - checkout processing');
            return;
        }

        const store = getCartStore();
        if (!store) return;

        try {
            const shippingRates = store.getShippingRates?.();
            if (!shippingRates || shippingRates.length === 0) return;

            const packageRates = shippingRates[0]?.shipping_rates || [];
            const selectedRate = packageRates.find(r => r.selected);

            if (!selectedRate) return;
            if (!isSpcbRate(selectedRate.rate_id)) {
                return;
            }

            // Find the corresponding radio in DOM
            const radios = document.querySelectorAll(
                '.wc-block-components-shipping-rates-control input[type="radio"]'
            );

            for (const radio of radios) {
                if (radio.value === selectedRate.rate_id && !radio.checked) {
                    log('Syncing DOM radio to match store selection:', selectedRate.rate_id);
                    radio.checked = true;
                    radio.dispatchEvent(new Event('change', { bubbles: true }));
                    break;
                }
            }
        } catch (e) {
            log('Error syncing DOM with store:', e.message);
        }
    }

    /**
     * Main synchronization function
     * Ensures shipping rates are properly loaded and selected
     * v1.16.22: Added pickup guard to prevent overwriting user's pickup selection
     */
    function performSync() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('performSync disabled (official flow)');
            return;
        }

        // v1.12.0: Skip sync if checkout is processing to prevent race conditions
        if (isCheckoutProcessing()) {
            log('Checkout processing - skipping sync');
            return;
        }

        // v1.16.22: NUNCA sobrescribir una selección de pickup del DOM
        // Si el usuario seleccionó retiro en tienda, respetar esa elección
        const domSelectedRadio = document.querySelector(
            '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
        );
        if (domSelectedRadio && isPickupMethod(domSelectedRadio.value)) {
            log('Pickup seleccionado en DOM - saltando sync para evitar interferencia');
            return;
        }

        if (!isStoreAvailable()) {
            log('Store not available yet, will retry...');
            scheduleRetry();
            return;
        }

        syncAttempts++;
        log(`Sync attempt ${syncAttempts}/${MAX_SYNC_ATTEMPTS}`);

        // Check if we're on a page that needs shipping (checkout)
        const isCheckout = document.body.classList.contains('woocommerce-checkout') ||
                          document.querySelector('.wc-block-checkout');

        if (!isCheckout) {
            log('Not on checkout page, skipping sync');
            return;
        }

        // Check current state
        const hasRates = hasValidShippingRates();
        const hasSelection = hasSelectedShippingRate();

        log('Current state - hasRates:', hasRates, 'hasSelection:', hasSelection);

        if (hasRates && hasSelection) {
            // Rates are loaded and a selection exists - just sync DOM
            syncDomWithStore();
            storeReady = true;
            log('Store is ready and synced');
            return;
        }

        if (hasRates && !hasSelection) {
            // Rates exist but nothing selected - select the first one
            const firstRateId = getFirstAvailableRateId();
            if (firstRateId) {
                forceSelectShippingRate(firstRateId);
                setTimeout(syncDomWithStore, 100);
                storeReady = true;
                log('Auto-selected first shipping rate:', firstRateId);
                return;
            }
        }

        // No rates or invalid state - try to refresh
        if (syncAttempts < MAX_SYNC_ATTEMPTS) {
            // NO invalidar el cart store - causa "carrito vacío" momentáneo y error "signal aborted"
            // WC Blocks maneja su propio caché internamente
            scheduleRetry();
        } else {
            log('Max sync attempts reached, giving up');
        }
    }

    /**
     * Schedule a retry of the sync process
     */
    function scheduleRetry() {
        if (syncAttempts >= MAX_SYNC_ATTEMPTS) return;

        setTimeout(() => {
            performSync();
        }, SYNC_RETRY_DELAY * Math.min(syncAttempts, 3));
    }

    /**
     * Subscribe to store changes
     * Monitors shipping rates updates and ensures sync
     */
    function subscribeToStoreChanges() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('subscribeToStoreChanges disabled (official flow)');
            return;
        }

        if (!isStoreAvailable()) return;

        let lastRateCount = 0;
        let lastSelectedId = null;

        try {
            window.wp.data.subscribe(() => {
                // v1.12.0: Skip if checkout is processing to prevent race conditions
                if (isCheckoutProcessing()) {
                    return;
                }

                // v1.18.3: Skip if branch-checkbox is handling refresh
                if (window.spcbBranchRefreshInProgress) {
                    return;
                }

                // v1.16.22: NUNCA interferir si el usuario tiene pickup seleccionado en el DOM
                const domSelectedRadio = document.querySelector(
                    '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
                );
                if (domSelectedRadio && isPickupMethod(domSelectedRadio.value)) {
                    return; // No hacer nada si hay pickup seleccionado
                }

                const store = getCartStore();
                if (!store) return;

                try {
                    const shippingRates = store.getShippingRates?.();
                    if (!shippingRates || shippingRates.length === 0) return;

                    const packageRates = shippingRates[0]?.shipping_rates || [];
                    const selectedRate = packageRates.find(r => r.selected);

                    // Check if shipping rates changed
                    if (packageRates.length !== lastRateCount) {
                        lastRateCount = packageRates.length;
                        log('Shipping rates changed, count:', packageRates.length);

                        // If rates appeared but nothing selected, select first
                        // v1.16.22: getFirstAvailableRateId() ahora respeta la selección del DOM
                        if (packageRates.length > 0 && !selectedRate) {
                            const preferredRateId = getFirstAvailableRateId();
                            if (preferredRateId) {
                                setTimeout(() => {
                                    forceSelectShippingRate(preferredRateId);
                                    syncDomWithStore();
                                }, 50);
                            }
                        }
                    }

                    // Check if selection changed
                    const currentSelectedId = selectedRate?.rate_id || null;
                    if (currentSelectedId !== lastSelectedId) {
                        lastSelectedId = currentSelectedId;
                        if (currentSelectedId) {
                            log('Selected shipping rate changed:', currentSelectedId);
                            setTimeout(syncDomWithStore, 50);
                        }
                    }
                } catch (e) {
                    // Ignore errors in subscribe callback
                }
            });

            log('Subscribed to store changes');
        } catch (e) {
            log('Error subscribing to store:', e.message);
        }
    }

    /**
     * Watch for WC Blocks components to be mounted
     * Uses MutationObserver to detect when checkout UI is ready
     */
    function watchForCheckoutMount() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('watchForCheckoutMount disabled (official flow)');
            return;
        }

        const observer = new MutationObserver((mutations, obs) => {
            const shippingRates = document.querySelector('.wc-block-components-shipping-rates-control');

            if (shippingRates) {
                log('Shipping rates control detected in DOM');
                obs.disconnect();

                // Give React more time to hydrate
                setTimeout(() => {
                    performSync();
                }, 500);

                // Additional sync attempts to handle async loading
                setTimeout(performSync, 1000);
                setTimeout(performSync, 2000);
            }
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Timeout after 10 seconds
        setTimeout(() => observer.disconnect(), 10000);
    }

    /**
     * Handle shipping method container errors
     * Removes "no options available" messages when rates exist
     * v1.16.22: Added pickup guard to prevent overwriting user's pickup selection
     */
    function handleShippingErrors() {
        if (!SHIPPING_SYNC_ENABLED) {
            log('handleShippingErrors disabled (official flow)');
            return;
        }

        const errorMessages = [
            'No hay ninguna opción disponible',
            'Lo siento, este pedido necesita',
            'No shipping options available',
            'Sorry, this order needs a shipping'
        ];

        const observer = new MutationObserver(() => {
            // v1.12.0: Skip if checkout is processing to prevent race conditions
            if (isCheckoutProcessing()) {
                return;
            }

            // v1.16.22: NUNCA interferir si el usuario tiene pickup seleccionado
            const domSelectedRadio = document.querySelector(
                '.wc-block-components-shipping-rates-control input[type="radio"]:checked'
            );
            if (domSelectedRadio && isPickupMethod(domSelectedRadio.value)) {
                return; // No hacer nada si hay pickup seleccionado
            }

            // Check for error messages in the shipping summary
            const summaryValue = document.querySelector(
                '.wc-block-components-totals-shipping .wc-block-components-totals-item__value'
            );

            if (summaryValue) {
                const text = summaryValue.textContent || '';
                const hasError = errorMessages.some(msg => text.includes(msg));

                if (hasError && hasValidShippingRates()) {
                    log('Found shipping error but rates exist, forcing sync...');

                    const rateId = getFirstAvailableRateId();
                    if (rateId) {
                        forceSelectShippingRate(rateId);
                        setTimeout(syncDomWithStore, 100);
                    }
                }
            }

            // Also check for errors in the shipping method section
            const shippingSection = document.querySelector(
                '.wc-block-checkout__shipping-option, .wc-block-checkout__shipping-fields'
            );

            if (shippingSection) {
                const text = shippingSection.textContent || '';
                const hasError = errorMessages.some(msg => text.includes(msg));

                if (hasError && hasValidShippingRates()) {
                    log('Found shipping section error but rates exist, forcing sync...');

                    const rateId = getFirstAvailableRateId();
                    if (rateId) {
                        forceSelectShippingRate(rateId);
                        setTimeout(syncDomWithStore, 100);
                    }
                }
            }
        });

        const target = document.querySelector('.wc-block-checkout, .wp-block-woocommerce-checkout');
        if (target) {
            observer.observe(target, {
                childList: true,
                subtree: true,
                characterData: true
            });
        }
    }

    /**
     * Initialize the store sync module
     * v1.16.22: Added pickup protection guards
     */
    function init() {
        log('Initializing Store Sync Module v1.16.22 (pickup-aware)');

        // v1.12.0: Initialize checkout processing listener first
        initCheckoutProcessingListener();

        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', onDomReady);
        } else {
            onDomReady();
        }
    }

    /**
     * Called when DOM is ready
     */
    function onDomReady() {
        log('DOM ready, starting sync process');

        if (!SHIPPING_SYNC_ENABLED) {
            log('Auto-shipping sync disabled; leaving selection to WooCommerce Blocks');
            return;
        }

        // Check if we're on checkout
        const isCheckout = document.body.classList.contains('woocommerce-checkout') ||
                          document.querySelector('.wc-block-checkout, .wp-block-woocommerce-checkout');

        if (!isCheckout) {
            log('Not on checkout page, module inactive');
            return;
        }

        // Start watching for checkout components
        watchForCheckoutMount();

        function initializeStore() {
            subscribeToStoreChanges();
            handleShippingErrors();
            performSync();
        }

        // Subscribe to store changes when store is available
        if (isStoreAvailable()) {
            initializeStore();
        } else {
            // Wait for store to be available
            const checkStore = setInterval(() => {
                if (isStoreAvailable()) {
                    clearInterval(checkStore);
                    initializeStore();
                }
            }, 100);

            // Give up after 5 seconds
            setTimeout(() => clearInterval(checkStore), 5000);
        }

        // Additional sync on window load (after all resources loaded)
        window.addEventListener('load', () => {
            setTimeout(performSync, 500);
            setTimeout(performSync, 1500);
        });
    }

    // Expose module for debugging
    window.spcbStoreSync = {
        isStoreAvailable,
        hasValidShippingRates,
        hasSelectedShippingRate,
        getFirstAvailableRateId,
        forceSelectShippingRate,
        invalidateCartStore,
        syncDomWithStore,
        performSync,
        triggerCartRefresh
    };

    // Initialize
    init();

    log('SPCB Store Sync Module v1.14.8 loaded');
})();
